import { Body1, Body1Strong, Caption1, ProgressBar, Subtitle1, makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkCircleFilled, CircleRegular } from '@fluentui/react-icons';

import { useReviewStyles } from '../shared';
import { getBasisLabel, getReviewProgress, getReviewStage } from '../../helpers';
import { TReview, TReviewBasis } from '../../types';

const useStyles = makeStyles({
  stages: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  stage: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  stageActive: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  done: {
    color: tokens.colorPaletteGreenForeground1,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
  },
});

type TProps = {
  review: TReview;
  bases: TReviewBasis[];
  now: number;
};

export const ReviewProgress = ({ review, bases, now }: TProps) => {
  const classes = useStyles();
  const reviewClasses = useReviewStyles();

  const progress = getReviewProgress(review, now);
  const stageIndex = getReviewStage(review.loadingMessages, progress);
  const documentsProcessed = Math.max(1, Math.round(review.documentsAnalyzed * progress));

  return (
    <div className={reviewClasses.surface}>
      <div className="flex flex-col gap-1">
        <Subtitle1>{review.name}</Subtitle1>
        <Caption1 className={reviewClasses.muted}>
          {[review.productName, review.scopeLabel].filter(Boolean).join(', ')}
        </Caption1>
      </div>

      <div className="flex flex-wrap items-center justify-start gap-2 text-left">
        {review.reviewBasisIds.map((id) => (
          <Body1 key={id} className={reviewClasses.muted}>
            {getBasisLabel(bases, id)}
          </Body1>
        ))}
      </div>

      <div className="flex flex-col gap-2 max-w-xl">
        <ProgressBar value={progress} thickness="large" />
        <Body1Strong>
          {documentsProcessed} of {review.documentsAnalyzed} documents processed
        </Body1Strong>
      </div>

      <div className={classes.stages}>
        {review.loadingMessages.map((message, index) => {
          const isDone = index < stageIndex;
          const isActive = index === stageIndex;
          return (
            <div key={message} className={`${classes.stage} ${isActive ? classes.stageActive : ''}`}>
              {isDone ? <CheckmarkCircleFilled className={classes.done} /> : <CircleRegular />}
              <span>{message}</span>
            </div>
          );
        })}
      </div>

      <Caption1 className={classes.hint}>You can leave this page. The review continues in the background.</Caption1>
    </div>
  );
};
